import { useEffect, useState } from 'react';
import api from './api';
import { ROLES, roleLabel, roleBadgeStyle } from './roles';

const CREATABLE_ROLES = [ROLES.USER, ROLES.RESELLER];

export default function ResellerPage({ onBack, onLogout }) {
  const [form, setForm] = useState({ Username: '', Password: '', Role: ROLES.USER, Email: '', Mobile: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);

  const name = sessionStorage.getItem('auth_name') || 'User';

  const loadUsers = () => {
    setUsersLoading(true);
    api.get('/Reseller/User/List')
      .then(res => setUsers(res.data))
      .catch(err => setUsersError(err.message))
      .finally(() => setUsersLoading(false));
  };

  useEffect(() => { loadUsers(); }, []);

  const set = (field) => (e) => {
    setSuccess(false);
    setError(null);
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(false);
    try {
      const fd = new FormData();
      fd.append('Username', form.Username);
      fd.append('Password', form.Password);
      fd.append('Role', form.Role);
      fd.append('Email', form.Email);
      fd.append('Mobile', form.Mobile);
      await api.post('/Admin/User/Create', fd);
      setSuccess(true);
      setForm({ Username: '', Password: '', Role: ROLES.USER, Email: '', Mobile: '' });
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message ?? err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#1a1a1a', color: '#e5e7eb', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '12px 24px',
        background: '#2d2d2d', borderBottom: '1px solid #444',
      }}>
        <span style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>Console Management</span>
        <div style={{ flex: 1 }} />
        <button onClick={onBack} style={{
          padding: '4px 12px', borderRadius: 6, border: 'none',
          background: '#374151', color: '#d1d5db', fontSize: 12,
          cursor: 'pointer', marginRight: 8,
        }}>
          ← Back
        </button>
        <span style={{ fontSize: 13, color: '#9ca3af', marginRight: 8 }}>{name}</span>
        <span style={{ ...roleBadgeStyle(ROLES.RESELLER), marginRight: 16 }}>{roleLabel(ROLES.RESELLER)}</span>
        <button onClick={async () => { try { await api.get('/Authentication/Logout'); } finally { onLogout(); } }} style={{
          padding: '4px 12px', borderRadius: 6, border: 'none',
          background: '#374151', color: '#d1d5db', fontSize: 12,
          cursor: 'pointer',
        }}>
          Logout
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '32px 24px', maxWidth: 900, margin: '0 auto' }}>
        <h2 style={{ margin: '0 0 24px', fontSize: 18, fontWeight: 600, color: '#f9fafb' }}>
          Create User
        </h2>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {success && (
            <div style={{ background: '#14532d', color: '#bbf7d0', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
              User created successfully.
            </div>
          )}
          {error && (
            <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
              {error}
            </div>
          )}

          <label style={labelStyle}>
            <span style={labelText}>Username *</span>
            <input required value={form.Username} onChange={set('Username')} style={inputStyle} />
          </label>

          <label style={labelStyle}>
            <span style={labelText}>Password *</span>
            <input type="password" required value={form.Password} onChange={set('Password')} style={inputStyle} />
          </label>

          <label style={labelStyle}>
            <span style={labelText}>Role</span>
            <select value={form.Role} onChange={set('Role')} style={inputStyle}>
              {CREATABLE_ROLES.map(r => (
                <option key={r} value={r}>{roleLabel(r)}</option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            <span style={labelText}>Email</span>
            <input type="email" value={form.Email} onChange={set('Email')} style={inputStyle} />
          </label>

          <label style={labelStyle}>
            <span style={labelText}>Mobile</span>
            <input value={form.Mobile} onChange={set('Mobile')} style={inputStyle} />
          </label>

          <button type="submit" disabled={submitting} style={{
            marginTop: 4, padding: '10px', borderRadius: 6, border: 'none',
            background: submitting ? '#1e3a5f88' : '#1e3a5f',
            color: '#93c5fd', fontWeight: 600, fontSize: 14,
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}>
            {submitting ? 'Creating...' : 'Create User'}
          </button>
        </form>

        {/* User List */}
        <div style={{ marginTop: 48 }}>
          <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600, color: '#f9fafb' }}>
            My Users
          </h2>
          {usersLoading && <div style={{ color: '#9ca3af' }}>Loading...</div>}
          {usersError && <div style={{ color: '#fca5a5' }}>Error: {usersError}</div>}
          {!usersLoading && !usersError && users.length === 0 && (
            <div style={{ color: '#6b7280' }}>No users found.</div>
          )}
          {!usersLoading && !usersError && users.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: '#6b7280', textAlign: 'left', borderBottom: '1px solid #374151' }}>
                  <th style={{ padding: '8px 12px' }}>Username</th>
                  <th style={{ padding: '8px 12px' }}>Name</th>
                  <th style={{ padding: '8px 12px' }}>Email</th>
                  <th style={{ padding: '8px 12px' }}>Mobile</th>
                  <th style={{ padding: '8px 12px' }}>Role</th>
                  <th style={{ padding: '8px 12px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} style={{ borderBottom: '1px solid #1f2937', color: '#d1d5db' }}>
                    <td style={{ padding: '8px 12px' }}>{u.username || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{u.name || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{u.email || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{u.mobile || '—'}</td>
                    <td style={{ padding: '8px 12px' }}><span style={roleBadgeStyle(u.level)}>{roleLabel(u.level)}</span></td>
                    <td style={{ padding: '8px 12px' }}>{u.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const labelStyle = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelText = { color: '#9ca3af', fontSize: 12 };
const inputStyle = {
  padding: '8px 10px', borderRadius: 6, border: '1px solid #555',
  background: '#1a1a1a', color: '#fff', fontSize: 13, outline: 'none',
};
